import { NavLink, useLocation } from 'react-router';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/features/auth/permissions';
import { useSession } from '@/features/auth/session';
import { useUiStore } from '@/stores/ui.store';
import { Avatar } from '@/ui/Avatar';
import { Logo, LogoMark } from '@/ui/Logo';
import { cn } from '@/lib/cn';
import { NAV } from './nav';

export function Sidebar() {
  const { t } = useTranslation();
  const { canAny } = usePermissions();
  const { data: session } = useSession();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const location = useLocation();

  /**
   * NavLink ignores the query string, so `/hr/attendance` and
   * `/hr/attendance?view=team` would both light up at once. Two entries
   * pointing at the same route with different tabs have to be told apart by
   * the `view` they ask for.
   */
  const isCurrent = (to: string) => {
    const [path, query] = to.split('?');
    if (location.pathname !== path) return false;
    const wanted = new URLSearchParams(query ?? '').get('view');
    return (new URLSearchParams(location.search).get('view') ?? null) === wanted;
  };

  const sections = NAV
    .map((section) => ({
      ...section,
      items: section.items.filter((i) => canAny(...i.permissions) || (session && i.also?.(session))),
    }))
    .filter((section) => section.items.length > 0);

  const topRole = session?.roles[0];

  return (
    <>
    {!collapsed && <button aria-label={t('nav.collapse')} onClick={toggle} className="fixed inset-0 z-30 bg-black/30 md:hidden" />}
    <nav
      aria-label={t('nav.mainNavigation')}
      className={cn(
        'flex h-full shrink-0 flex-col bg-sidebar py-5 transition-[width] duration-200',
        collapsed ? 'w-[76px]' : 'fixed inset-y-0 start-0 z-40 w-[min(290px,85vw)] md:relative md:z-auto md:w-[290px]',
      )}
    >
      <div className="flex shrink-0 items-center px-5 pb-5">
        {collapsed ? <LogoMark /> : <Logo onDark />}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3.5">
      {sections.map((section) => (
        <div key={section.labelKey} className="flex flex-col gap-0.5">
          {!collapsed && (
            <p className="px-2.5 pt-3 pb-1.5 text-[10px] font-semibold tracking-[0.06em] text-sidebar-label uppercase">
              {t(section.labelKey)}
            </p>
          )}
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? t(item.labelKey) : undefined}
              aria-current={isCurrent(item.to) ? 'page' : undefined}
              className={cn(
                'flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13px] transition-colors',
                isCurrent(item.to)
                  ? 'bg-secondary font-semibold text-white'
                  : 'font-medium text-sidebar-item hover:bg-white/5 hover:text-white',
                collapsed && 'justify-center px-0',
              )}
            >
              <item.icon className="size-[18px] shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
            </NavLink>
          ))}
        </div>
      ))}
      </div>

      <div className="flex shrink-0 flex-col gap-2 border-t border-sidebar-line px-3.5 pt-3">
        <button
          onClick={toggle}
          className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium text-sidebar-item hover:bg-white/5 hover:text-white"
          aria-label={t(collapsed ? 'nav.expand' : 'nav.collapse')}
        >
          {collapsed
            ? <PanelLeftOpen className="size-[18px] shrink-0 rtl:rotate-180" aria-hidden />
            : <PanelLeftClose className="size-[18px] shrink-0 rtl:rotate-180" aria-hidden />}
          {!collapsed && t('nav.collapse')}
        </button>

        {session && (
          <div className={cn(
            'flex items-center gap-2.5 px-2 pt-1',
            collapsed && 'justify-center px-0',
          )}>
            <Avatar name={session.user.name} size={32} />
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-white">{session.user.name}</p>
                {topRole && (
                  <p className="truncate text-[10px] font-medium text-success">
                    {t(`roles.${topRole}`, { defaultValue: topRole })}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    </>
  );
}
